import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from '../Button';
import Progress from '../Progress';
import Item from '../Item';
import Icon from '../Icon';
import { useControlledInputs } from '../../common/hooks';

/** 单文件数据映射 */
const mapDefaultToValue = v => v;
const mapValuetoValue = e => {
  if (!e) {
    return null;
  }
  return e.target?.files[0];
};

/**
 * 文件上传模块
 */
export default function Uploader({
  defaultValue,
  onChange,
  hasError,
  buttonRender,
  type,
  progress,
  showProgress,
  success,
  error,
  className,
  style,
  mini,
  deletable = false,
  ...others
}) {
  const inputRef = useRef(null);
  const { value, handleChange } = useControlledInputs({
    defaultValue,
    onChange,
    mapDefaultToValue,
    mapValuetoValue
  });

  // 触发文件选择的按钮
  const selectFiles = useCallback(() => {
    inputRef.current.click();
  }, []);

  const onFileDelete = useCallback(() => {
    inputRef.current.value = null;
    handleChange(null);
  }, []);
  // 样式
  const classes = classNames('uploader', className);
  const inputClasses = classNames('input file-name', {
    error: hasError,
    mini
  });

  return (
    <div className={classes} style={style}>
      <div className="file">
        <input
          type="file"
          ref={inputRef}
          onChange={e => {
            // 避免取消清空文件
            if (mapValuetoValue(e)) {
              handleChange(e);
            }
          }}
          className="file-hide"
        />
        <Item show={type === 'input'}>
          <>
            <div className={inputClasses} onClick={selectFiles}>
              {value?.name || ''}
            </div>
            <div onClick={selectFiles}>
              {buttonRender || (
                <Button type="primary" mini={mini}>
                  选择文件
                </Button>
              )}
            </div>
          </>
        </Item>
        {/* button类型 */}
        <Item show={type === 'button'}>
          <div className="button-wrap" onClick={selectFiles}>
            {buttonRender || (
              <div className="btn-wrapper">
                <Icon type="upload" />
                选择文件
              </div>
            )}
            <Item show={value?.name}>
              <div className="file-item">
                <div className="file-name">
                  <Icon type="fileLink" className="mgr5" mini />
                  {value?.name}
                  {/* 状态 */}
                  <Item show={success}>
                    <span className="success status">上传成功</span>
                  </Item>
                  <Item show={error}>
                    <span className="error status">上传失败</span>
                  </Item>
                  <span></span>
                </div>
                {/* 删除按钮 */}
                {deletable ? (
                  <Icon
                    onClick={onFileDelete}
                    type="deleteAll"
                    className="delete-btn mgr5"
                    mini
                  />
                ) : null}
              </div>
            </Item>
          </div>
        </Item>
      </div>
      <Item show={showProgress}>
        <Progress
          percent={progress}
          {...others}
          status={error ? 'exception' : ''}
          strokeColor={error ? '#ff4d4f' : ''}
          strokeWidth={3}
        />
      </Item>
    </div>
  );
}

Uploader.defaultProps = {
  onChange: v => v,
  type: 'input',
  showProgress: false,
  success: false,
  error: false
};
Uploader.propTypes = {
  /** 事件回调 */
  onChange: PropTypes.func,
  /** 默认值 */
  defaultValue: PropTypes.any,
  /** 是否Error, 自带error样式 */
  hasError: PropTypes.bool,
  /** 自定义文件上传的class */
  className: PropTypes.string,
  /** 自定义选择文件的触发按钮 */
  buttonRender: PropTypes.any,
  /** 默认提供两种类型的文件上传 */
  type: PropTypes.oneOf(['input', 'button']),
  /** 当前上传进度 */
  progress: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** 展示进度 */
  showProgress: PropTypes.bool,
  /** 文件上传成功 */
  success: PropTypes.bool,
  /** 文件上传失败 */
  error: PropTypes.bool,
  /** mini版 */
  mini: PropTypes.bool,
  /** 是否可删除 */
  deletable: PropTypes.bool
};
